import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWRImmutable from 'swr/immutable';
import { useDispatch } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import {
  ContextTypes,
  Loading,
  TwoFactorAuthenticationMethodVerificationStepsOverrideStates,
  TwoFactorAuthenticationSetup,
  TwoFactorAuthenticationSetupProps,
} from '@tradingblock/components';
import Config from '../../../config';
import { useApi } from '../../../context/Api';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { GetTwoFactorAuthenticationDetailsUrl } from '@tradingblock/api';
import { PhoneModel, SiteGrp, TwoFactorAuthenticationStates } from '@tradingblock/types';
import { TwoFactorAuthenticationActions, UIActions } from '../../../data/global/actions';
import { AccountActions } from '../../../data/global/actions/AccountActions';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';

const StyledTwoFactorAuthenticationSection = styled.div`
  .header-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 1rem;
  }
    
  .header-wrapper h5, .method-header-wrapper h6 {
    margin-bottom: 0;
  }

  .main-content-wrapper {
    margin-top: 1rem;
  }

  .main-content-wrapper .verified {
    color: var(--teal);
    margin-right: 0.25rem;
  }

  .main-content-wrapper .verified i {
    margin-right: 0.25rem;
  }

  .method-header-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .method-header-wrapper h6 {
    margin-right: 0.25rem;
  }

  .email-change {
    margin-bottom: 0.5rem;
  }

  .contact-change-btn:hover, .update-email-btn:hover, .select-diff-phone-btn:hover, .send-new-code-btn:hover {
    color: white;
  }

  .sms-method-section {
    margin-bottom: 1rem;
  }

  .sms-method-section p {
    margin-bottom: 0;
  }
`;

const TwoFactorAuthenticationSection = () => {
  const api = useApi();

  const dispatch = useDispatch();

  const twoFactorAuthenticationDetails = useStateSelector(s => s.twoFactorAuthentication.details);
  const twoFactorAuthenticationState = useStateSelector(s => s.account.profile.current && s.account.profile.current.twoFactorAuthenticationState);
  const primaryAccountHolderEmail = useStateSelector(s => s.accountManagement.accountManagementDetails.details.accountHolders[0].email);
  const primaryAccountHolderPhones = useStateSelector(s => s.accountManagement.accountManagementDetails.details.accountHolders[0].phones);
  const accountId = useStateSelector(s => s.account.accountId);
  const isLoadingAccountManagementDetails = useStateSelector(s => s.accountManagement.accountManagementDetails.isFetching);
  const errorGettingAccountManagementDetails = useStateSelector(s => s.accountManagement.accountManagementDetails.error);
  const isLoadingUserProfile = useStateSelector(s => s.account.profile.isFetching);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [smsTwoFactorAuthenticationMethodVerifiedPhoneNumber, setSmsTwoFactorAuthenticationMethodVerifiedPhoneNumber] = useState<string | null>(null);
  const [customTwoFactorAuthenticationSetupComponentProps, setCustomTwoFactorAuthenticationSetupComponentProps] = useState<Partial<TwoFactorAuthenticationSetupProps> | {}>({});
  const [showTwoFactorAuthenticationSetupComponent, setShowTwoFactorAuthenticationSetupComponent] = useState<boolean>(false);

  const prevTwoFactorAuthenticationState = useRef<TwoFactorAuthenticationStates | null | undefined>(twoFactorAuthenticationState);

  const genericErrorMsg = 'There was an issue on our end. Try moving to a different tab within the account management block and try again. If the issue persists, please contact Customer Service.';

  const siteGrp = Config.siteGroup as SiteGrp;

  const uuidForGetTwoFactorAuthenticationDetailsKey = useMemo(() => uuidV4(), []);

  const {
    isLoading: isLoadingTwoFactorAuthenticationDetails,
    data: getTwoFactoAuthenticationDetailsResponse,
    error: errorGettingTwoFactorAuthenticationDetails,
    isValidating: isValidatingTwoFactorAuthenticationDetails,
    mutate: getTwoFactorAuthenticationDetails,
  } = useSWRImmutable(
    `${Config.tradingApi}/${GetTwoFactorAuthenticationDetailsUrl}${uuidForGetTwoFactorAuthenticationDetailsKey}`,
    api.twoFactorAuthentication.getDetails,
    { revalidateOnFocus: false, shouldRetryOnError: false, revalidateOnMount: true }
  );

  useEffect(() => {
    dispatch(AccountActions.requestUserProfile());
  }, []);

  useEffect(() => {
    if (prevTwoFactorAuthenticationState.current && twoFactorAuthenticationState !== prevTwoFactorAuthenticationState.current) {
      getTwoFactorAuthenticationDetails();
    }
  }, [twoFactorAuthenticationState]);

  useEffect(() => {
    if (!twoFactorAuthenticationState) {
      setErrorMsg(genericErrorMsg);
    }
  }, [twoFactorAuthenticationState]);

  useEffect(() => {
    prevTwoFactorAuthenticationState.current = twoFactorAuthenticationState;
  }, [twoFactorAuthenticationState]);

  useEffect(() => {
    if (errorGettingTwoFactorAuthenticationDetails || errorGettingAccountManagementDetails) {
      setErrorMsg(genericErrorMsg);
    } else {
      setErrorMsg(null);
    }
  }, [errorGettingTwoFactorAuthenticationDetails, errorGettingAccountManagementDetails]);

  useEffect(() => {
    if (
      getTwoFactoAuthenticationDetailsResponse &&
      getTwoFactoAuthenticationDetailsResponse.responseCode === 0 &&
      getTwoFactoAuthenticationDetailsResponse.payload
    ) {
      const { smsVerifiedPhoneType, isEmailVerified, voiceVerifiedPhoneType } = getTwoFactoAuthenticationDetailsResponse.payload;

      dispatch(TwoFactorAuthenticationActions.receiveDetails({ smsVerifiedPhoneType, isEmailVerified, voiceVerifiedPhoneType }));

      const phoneNumberDataVerifiedAsSmsTwoFactorAuthenticationMethod = (primaryAccountHolderPhones.find((primaryAccountHolderPhone) => getTwoFactoAuthenticationDetailsResponse.payload.smsVerifiedPhoneType === primaryAccountHolderPhone.type)) as PhoneModel;

      if (phoneNumberDataVerifiedAsSmsTwoFactorAuthenticationMethod) {
        setSmsTwoFactorAuthenticationMethodVerifiedPhoneNumber(phoneNumberDataVerifiedAsSmsTwoFactorAuthenticationMethod.phoneNumber);
      }
    } else if (
      getTwoFactoAuthenticationDetailsResponse &&
      (
        getTwoFactoAuthenticationDetailsResponse.responseCode !== 0 ||
        !getTwoFactoAuthenticationDetailsResponse.payload
      )
    ) {
      setErrorMsg(genericErrorMsg);
    }
  }, [getTwoFactoAuthenticationDetailsResponse]);

  useEffect(() => {
    if (
      !isValidatingTwoFactorAuthenticationDetails &&
      !isLoadingTwoFactorAuthenticationDetails &&
      !isLoadingAccountManagementDetails &&
      !isLoadingUserProfile &&
      !errorMsg &&
      (
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndEmailMethodNotVerified ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndNoVerifiedMethods ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndSmsMethodNotVerified ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndEmailMethodNotVerified ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndNoVerifiedMethods ||
        twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndSmsMethodNotVerified
      )
    ) {
      setShowTwoFactorAuthenticationSetupComponent(true);
    } else {
      setShowTwoFactorAuthenticationSetupComponent(false);
    }
  }, [
    twoFactorAuthenticationState,
    isLoadingTwoFactorAuthenticationDetails,
    isValidatingTwoFactorAuthenticationDetails,
    errorMsg,
    isLoadingAccountManagementDetails,
    isLoadingUserProfile,
  ]);

  const handleVerifyEmailTwoFactorAuthenticationMethodBtnClick = () => {
    setShowTwoFactorAuthenticationSetupComponent(true);

    setCustomTwoFactorAuthenticationSetupComponentProps({
      twoFactorAuthenticationMethodVerificationStepsOverrideState: TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedEmailTwoFactorAuthenticationMethodVerification,
      allowAbilityToCancel: true,
      handleCancel: () => {
        getTwoFactorAuthenticationDetails();
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
        dispatch(AccountActions.requestUserProfile());
        setShowTwoFactorAuthenticationSetupComponent(false);
        setCustomTwoFactorAuthenticationSetupComponentProps({});
      },
    });
  };

  const handleVerifySmsTwoFactorAuthenticationMethodBtnClick = () => {
    setShowTwoFactorAuthenticationSetupComponent(true);

    setCustomTwoFactorAuthenticationSetupComponentProps({
      twoFactorAuthenticationMethodVerificationStepsOverrideState: TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedSmsTwoFactorAuthenticationMethodVerification,
      allowAbilityToCancel: true,
      handleCancel: () => {
        getTwoFactorAuthenticationDetails();
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
        dispatch(AccountActions.requestUserProfile());
        setShowTwoFactorAuthenticationSetupComponent(false);
        setCustomTwoFactorAuthenticationSetupComponentProps({});
      },
    });
  };

  const handleUpdateSmsTwoFactorAuthenticationMethodBtnClick = () => {
    setShowTwoFactorAuthenticationSetupComponent(true);

    const smsTwoFactorAuthenticationMethodVerifiedPhoneData = (primaryAccountHolderPhones
    .find((primaryAccountHolderPhone) =>
      primaryAccountHolderPhone.type === (twoFactorAuthenticationDetails && twoFactorAuthenticationDetails.smsVerifiedPhoneType)
    ) as PhoneModel);

    setCustomTwoFactorAuthenticationSetupComponentProps({
      twoFactorAuthenticationMethodVerificationStepsOverrideState: TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedSmsTwoFactorAuthenticationMethodVerification,
      allowAbilityToCancel: true,
      handleCancel: () => {
        getTwoFactorAuthenticationDetails();
        dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
        dispatch(AccountActions.requestUserProfile());
        setShowTwoFactorAuthenticationSetupComponent(false);
        setCustomTwoFactorAuthenticationSetupComponentProps({});
      },
      smsTwoFactorAuthenticationMethodVerifiedPhoneData,
    });
  };

  return (
    <>
      <StyledTwoFactorAuthenticationSection>
        <div className="header-wrapper">
          <i className="fal fa-mobile-alt fa-lg mr-2 dashboard-icon" />
          <h5>Two-factor Authentication</h5>
        </div>

        {
          (
            isLoadingTwoFactorAuthenticationDetails ||
            isValidatingTwoFactorAuthenticationDetails ||
            isLoadingAccountManagementDetails ||
            isLoadingUserProfile
          ) &&
          (
            <div className="loading-container"><Loading /></div>
          )
        }

        {
          (
            errorMsg &&
            !isLoadingTwoFactorAuthenticationDetails &&
            !isValidatingTwoFactorAuthenticationDetails &&
            !isLoadingAccountManagementDetails &&
            !isLoadingUserProfile
          ) &&
          (
            <p className="error">{errorMsg}</p>
          )
        }

        {
          (
            !showTwoFactorAuthenticationSetupComponent &&
            !isLoadingTwoFactorAuthenticationDetails &&
            !isValidatingTwoFactorAuthenticationDetails &&
            !isLoadingAccountManagementDetails &&
            !isLoadingUserProfile &&
            twoFactorAuthenticationDetails &&
            Object.keys(twoFactorAuthenticationDetails).length > 0 &&
            !errorMsg
          ) &&
          (
            <div className="main-content-wrapper">
              <p>You have enabled two-factor authentication.</p>

              <div className="sms-method-section">
                <div className="method-header-wrapper">
                  <h6>
                    Text Method
                  </h6>
                  {
                    twoFactorAuthenticationDetails.smsVerifiedPhoneType ?
                    (
                      <button
                      type="button"
                      className="mute txt-sm"
                      onClick={handleUpdateSmsTwoFactorAuthenticationMethodBtnClick}
                      >
                        (Update)
                      </button>
                    ) :
                    (
                      <button
                      type="button"
                      className="mute txt-sm"
                      onClick={handleVerifySmsTwoFactorAuthenticationMethodBtnClick}
                      >
                        (Verify)
                      </button>
                    )
                  }
                </div>
                {
                  twoFactorAuthenticationDetails.smsVerifiedPhoneType && (
                    <p>
                      <span className="verified">
                        <i className="fal fa-check" />
                        Verified
                      </span>
                      - {smsTwoFactorAuthenticationMethodVerifiedPhoneNumber}
                    </p>
                  )
                }
              </div>
              <div className="method-header-wrapper">
                <h6>
                  Email Method
                </h6>
                {!twoFactorAuthenticationDetails.isEmailVerified && (
                  <button
                  type="button"
                  className="mute txt-sm"
                  onClick={handleVerifyEmailTwoFactorAuthenticationMethodBtnClick}
                  >
                    (Verify)
                  </button>
                )}
              </div>
              {twoFactorAuthenticationDetails.isEmailVerified && (
                <>
                  <p className="txt-sm mute email-change">
                    In order to change the email tied to this verified two-factor authentication method you must first change the primary account holder's email. Once the primary account holder's email is changed you can come back to re-verify this two-factor authentication method with the new email. Click <button type="button" onClick={() => dispatch(UIActions.setEmailUpdateModalData({ show: true, accountHolderIndex: 0 }))}>here</button> to request a change in the primary account holder's email.
                  </p>
                  <p>
                    <span className="verified">
                      <i className="fal fa-check" />
                      Verified
                    </span>
                    - {primaryAccountHolderEmail}
                  </p>
                </>
              )}
            </div>
          )
        }

        {showTwoFactorAuthenticationSetupComponent && (
          <TwoFactorAuthenticationSetup
            primaryBtnClassName="btn btn-primary"
            handleEmailUpdate={() => {
              dispatch(UIActions.setEmailUpdateModalData({ show: true, accountHolderIndex: 0 }));
            }}
            handlePhoneUpdate={() => {
              dispatch(UIActions.setPhoneUpdateModalData({ show: true, accountHolderIndex: 0 }));
            }}
            api={api}
            accountId={accountId}
            handleSetupComplete={() => {
              getTwoFactorAuthenticationDetails();
              dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId }));
              dispatch(AccountActions.requestUserProfile());
              setShowTwoFactorAuthenticationSetupComponent(false);
              setCustomTwoFactorAuthenticationSetupComponentProps({});
            }}
            rootRestHubUrl={Config.tradingApi}
            context={ContextTypes.accountManagementBlock}
            customRequestUserProfile={() => {
              dispatch(AccountActions.requestUserProfile());
            }}
            receiveAccountManagementDetails={(accountManagementDetailsData) => {
              dispatch(AccountManagementDataActions.receiveAccountManagementDetails({ response: accountManagementDetailsData }));
              dispatch(AccountManagementDataActions.requestPendingAccountManagementUpdates({ accountId }));
            }}
            siteGrp={siteGrp}
            {...customTwoFactorAuthenticationSetupComponentProps}
          />
        )}
      </StyledTwoFactorAuthenticationSection>
    </>
  );
};

export default TwoFactorAuthenticationSection;
